import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Heart,
  Clock,
  Users,
  BookOpen,
  Download,
  Headphones,
  MessageSquare,
  Star,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const Support = () => {
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 0,
    comment: '',
    email: ''
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const { handleInstallClick, isInstallable, appInstalled } = usePwaInstall();
  const { user } = useAuth();

  const handleWhatsAppNutritionists = () => {
    window.open('https://wa.me/661680582', '_blank');
  };

  const handleWhatsAppTechnical = () => {
    window.open('https://wa.me/605216064', '_blank');
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedbackForm.rating || !feedbackForm.comment) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      const { error } = await supabase
        .from('user_feedback')
        .insert({
          user_id: user?.id,
          rating: feedbackForm.rating,
          comment: feedbackForm.comment,
          email: feedbackForm.email || user?.email,
          type: 'general'
        });

      if (error) throw error;

      toast.success('¡Gracias por tu feedback! Lo hemos recibido correctamente.');
      setFeedbackForm({ rating: 0, comment: '', email: '' });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Error al enviar el feedback. Inténtalo de nuevo.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        duration: 0.5
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <ProtectedRoute>
      <Layout>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-8"
        >
          <motion.div variants={itemVariants} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-vegana-primary mb-4">Centro de Soporte</h1>
            <p className="text-lg text-gray-600">
              ¿Necesitas ayuda? Estamos aquí para apoyarte durante toda tu experiencia en Guía Vegana APP
            </p>
          </motion.div>

          {/* Support Banners */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nutritionists Banner */}
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-green-800">Consulta Nutricionistas</CardTitle>
                <CardDescription className="text-green-700">
                  Habla con nuestros expertos en nutrición vegana, disponibles todos los días de la semana
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                  <Clock className="h-4 w-4" />
                  <span>Lun-dom 9:00-18:00</span>
                </div>
                <Button 
                  onClick={handleWhatsAppNutritionists}
                  className="bg-[#25D366] hover:bg-[#1fb855] text-white w-full"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  WhatsApp
                </Button>
              </CardContent>
            </Card>

            {/* Technical Support Banner */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                  <Headphones className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-blue-800">Soporte Técnico</CardTitle>
                <CardDescription className="text-blue-700">
                  Ayuda con problemas técnicos y de la aplicación
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                  <Clock className="h-4 w-4" />
                  <span>Lun-dom 9:00-18:00</span>
                </div>
                <Button 
                  onClick={handleWhatsAppTechnical}
                  className="bg-[#25D366] hover:bg-[#1fb855] text-white w-full"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  WhatsApp
                </Button>
              </CardContent>
            </Card>

            {/* App Installation Banner */}
            {!appInstalled && (
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mb-4">
                    <Download className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-purple-800">Instalar App</CardTitle>
                  <CardDescription className="text-purple-700">
                    Instala la app para acceso más rápido y funciones offline
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button 
                    onClick={handleInstallClick}
                    disabled={!isInstallable}
                    className="bg-purple-500 hover:bg-purple-600 text-white w-full"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Instalar Aplicación
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Feedback Form */}
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardHeader>
                <CardTitle className="text-xl text-orange-800 flex items-center gap-2">
                  <Star className="h-6 w-6" />
                  Danos tu Opinión
                </CardTitle>
                <CardDescription className="text-orange-700">
                  Tu feedback nos ayuda a mejorar la experiencia
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Calificación *
                    </label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFeedbackForm(prev => ({ ...prev, rating: star }))}
                          className={`p-1 ${feedbackForm.rating >= star ? 'text-yellow-500' : 'text-gray-300'}`}
                        >
                          <Star className="h-6 w-6 fill-current" />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Comentario *
                    </label>
                    <Textarea
                      value={feedbackForm.comment}
                      onChange={(e) => setFeedbackForm(prev => ({ ...prev, comment: e.target.value }))}
                      placeholder="Cuéntanos qué te parece la app..."
                      rows={3}
                      className="border-orange-200 focus:border-orange-400"
                      required
                    />
                  </div>
                  
                  {!user?.email && (
                    <div>
                      <label className="block text-sm font-medium text-orange-800 mb-2">
                        Email (opcional)
                      </label>
                      <Input
                        type="email"
                        value={feedbackForm.email}
                        onChange={(e) => setFeedbackForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="tu@email.com"
                        className="border-orange-200 focus:border-orange-400"
                      />
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmittingFeedback}
                    className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed text-white font-medium rounded-md border-0 shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
                    style={{ color: 'white', backgroundColor: isSubmittingFeedback ? '#fed7aa' : '#f97316' }}
                  >
                    <span className="text-white font-medium">
                      {isSubmittingFeedback ? 'Enviando...' : 'Enviar Feedback'}
                    </span>
                  </button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Additional Resources */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Recursos Adicionales</CardTitle>
                <CardDescription>
                  Más formas de obtener ayuda y mejorar tu experiencia
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-auto flex-col gap-2 p-6" asChild>
                    <a href="/tutoriales">
                      <BookOpen className="h-8 w-8 text-vegana-primary" />
                      <div>
                        <div className="font-medium">Guías y Tutoriales</div>
                        <div className="text-sm text-gray-500">Aprende paso a paso</div>
                      </div>
                    </a>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col gap-2 p-6" onClick={() => window.open("https://t.me/+019yrxUsSjxhZjE0", "_blank")}>
                    <Users className="h-8 w-8 text-vegana-primary" />
                    <div>
                      <div className="font-medium">Comunidad</div>
                      <div className="text-sm text-gray-500">Conecta con otros usuarios</div>
                    </div>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </Layout>
    </ProtectedRoute>
  );
};

export default Support;
